import AvisoDeError from '../../../nucleo/avisos/AvisoDeError.jsx';
import { ListadoCargando, ListadoConError } from '../../../nucleo/listados/EstadosDeListado.jsx';

/*
 * RNF-112 · CARGA EN PARALELO Y FALLA POR TARJETA.
 *
 * Cada tarjeta del Tablero tiene SU estado de carga y SU estado de error con
 * «Reintentar»: una fuente caída apaga SU tarjeta y nada más — ni el Tablero
 * entero ni la navegación. Y jamás se muestra una cifra vieja como si fuera
 * actual: mientras no hay respuesta no hay cifra.
 *
 * Los dos estados se dibujan con las piezas del núcleo (D-83, D-86, RF-139): el
 * esqueleto de carga y el aviso único de error con su código atenuado al final. No
 * se escribe aquí un segundo formato de error.
 */

/** Esqueleto de carga con el título de la tarjeta, para que la pantalla no salte. */
export function TarjetaCargando({ titulo, renglones = 3, dato }) {
  return (
    <section aria-label={titulo} data-prueba={dato}>
      <div className="gtit">{titulo}</div>
      <ListadoCargando renglones={renglones} texto={`Cargando ${titulo.toLowerCase()}…`} />
    </section>
  );
}

/**
 * Error de UNA fuente. El resto del Tablero sigue vivo y navegable, y el texto lo
 * dice para que nadie crea que la aplicación se cayó.
 */
export function TarjetaConError({ titulo, error, alReintentar, dato, mensaje = null }) {
  return (
    <section aria-label={titulo} data-prueba={dato}>
      <div className="gtit">{titulo}</div>
      {mensaje
        ? (
          <AvisoDeError
            mensaje={mensaje}
            datoDePrueba="listado-error"
            acciones={(
              <button className="btn sec" type="button" onClick={alReintentar} data-prueba="listado-reintentar">
                Reintentar
              </button>
            )}
          />
        )
        : <ListadoConError error={error} alReintentar={alReintentar} />}
      <div className="ayuda" style={{ margin: '-4px 12px 10px' }}>
        Lo demás del tablero sigue funcionando.
      </div>
    </section>
  );
}

/* ==========================================================================
 * FIX del 2026-10-08 · LOS MISMOS CUATRO ESTADOS, EN EL TAMAÑO DE UNA CIFRA
 * (D-323, RN-622, RNF-112).
 *
 * Las cifras de cabecera (T-5) no tenían estados: pintaban «—» mientras cargaban
 * y colgaban el aviso de fallo en la misma nota donde decían «aún no tienes
 * cuentas». Por eso un error y una ausencia se veían casi igual, que es
 * exactamente lo que RN-623 prohíbe.
 *
 * Estas dos piezas viven AQUÍ, junto a las de las tarjetas, para que el Tablero
 * tenga UN solo mecanismo de estados y no dos: lo que cambia es el tamaño del
 * hueco que ocupan, nunca la regla.
 * ========================================================================== */

/** (a) CARGANDO · ni cifra, ni cero, ni «—», ni texto de vacío (RN-622a, RN-141). */
export function CifraCargando({ rotulo, dato }) {
  return (
    <div className="cifra" data-prueba={dato} aria-busy="true">
      <span className="rot">{rotulo}</span>
      <span className="val" data-prueba={`${dato}-cargando`} aria-hidden="true">
        <span
          style={{
            display: 'inline-block', height: 18, width: 120, borderRadius: 6, background: 'var(--bor)',
          }}
        />
      </span>
      <span className="nota3">Cargando…</span>
    </div>
  );
}

/**
 * (b) ERROR DE LECTURA · dice que no se pudo cargar y ofrece reintentar. JAMÁS el
 * texto de vacío, jamás un cero y jamás un «—» (RN-622b, RN-623, RN-624).
 *
 * `alReintentar` relanza SOLO la consulta de esta cifra: las demás ni se enteran
 * y el Tablero no se recarga (RNF-112, CA-14-28).
 *
 * Los dos orígenes del error —la consulta falló, o respondió algo que no se pudo
 * leer— se ven IGUAL en pantalla, a propósito: a quien usa la aplicación no se le
 * explica la forma de una respuesta.
 */
export function CifraConError({ rotulo, mensaje, error, alReintentar, dato }) {
  return (
    <div className="cifra" data-prueba={dato}>
      <span className="rot">{rotulo}</span>
      <div data-prueba={`${dato}-con-error`}>
        {/* El texto propio de la cifra va SIEMPRE y es el mismo haya fallado la
            red o haya llegado una respuesta que no se pudo leer: lo que la
            persona necesita saber es que ese dato no se cargó y que puede
            reintentarlo. Cuando además hay un error con mensaje del servidor, se
            muestra debajo con su código atenuado, que es lo que sirve a soporte
            (RF-139, RN-333). */}
        <span className="nota3" style={{ color: 'var(--err)' }}>{mensaje}</span>
        <AvisoDeError
          error={error ?? null}
          margen="6px 0 0"
          datoDePrueba={`${dato}-aviso`}
          acciones={(
            <button
              className="btn sec"
              type="button"
              onClick={alReintentar}
              data-prueba={`${dato}-reintentar`}
            >
              Reintentar
            </button>
          )}
        />
        {/* Si el error no traía texto propio —respuesta ilegible—, el aviso del
            núcleo no se dibuja (RN-348c) y el reintento quedaría sin botón. */}
        {!error && (
          <button
            className="btn sec"
            type="button"
            onClick={alReintentar}
            data-prueba={`${dato}-reintentar`}
          >
            Reintentar
          </button>
        )}
      </div>
    </div>
  );
}
